import { useOrganization, useUser } from "@clerk/expo";
import { useMediaQuery } from "@gluestack-ui/utils/hooks";
import { useLocalSearchParams, useRouter } from "expo-router";
import {
  Archive,
  BarChart3,
  Copy,
  EllipsisVertical,
  FileSpreadsheet,
  FileText,
  FileType2,
  Rocket,
  RotateCcw,
  Share2,
  Trash2,
  UserPlus,
} from "lucide-react-native";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { Run, RunResults, RunResultsSkeleton } from "@/components/runs";
import {
  CreateStudyDialog,
  Groups,
  ShareModal,
  ShareResultsModal,
  StudyEditor,
} from "@/components/studies";
import {
  ActionMenu,
  ArchivedBanner,
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  Center,
  DeleteDialog,
  ErrorWithRetry,
  HStack,
  Skeleton,
  StepIndicator,
  Text,
  VStack,
} from "@/components/ui";
import MobileSummaryCard from "@/components/ui/MobileSummaryCard";
import { RunProvider, StudyProvider, useLocalization } from "@/hooks";
import API from "@/hooks/api";

/* ── Helpers ───────────────────────────────────── */

const STEP_TO_TAB = ["audience", "questions", "results"] as const;

const TAB_TO_STEP: Record<string, number> = {
  audience: 0,
  questions: 1,
  results: 2,
};

/* ── Component ─────────────────────────────────── */

const Study: FC = () => {
  const { browserLocale, i18n } = useLocalization();
  const router = useRouter();
  const { user } = useUser();
  const { organization, membership } = useOrganization();
  const isAdmin = user?.publicMetadata?.admin === true;
  const [isDesktop] = useMediaQuery({ minWidth: 1024 });
  const { studyId, tab } = useLocalSearchParams<{
    studyId: string;
    tab?: string;
  }>();

  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [completedRunId, setCompletedRunId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(
    TAB_TO_STEP[tab || "audience"] ?? 0
  );
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalRespondents, setTotalRespondents] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const hasNavigated = useRef(false);
  const hasSyncedTab = useRef(!!tab);
  const prevStep = useRef(currentStep);

  const estimatedMinutes = useMemo(
    () =>
      Math.max(1, Math.ceil((totalQuestions * totalRespondents * 0.1) / 60)),
    [totalQuestions, totalRespondents]
  );

  const { error, loading, refresh, study } = API.Studies.useStudy(studyId);
  const isArchived = !!study?.archived_at;
  const isReadOnly = !study?.can_edit && !isAdmin;
  const canDelete =
    isAdmin || !organization || membership?.role === "org:admin";
  const {
    error: triggerError,
    loading: triggering,
    runId,
    triggerRun,
  } = API.Studies.useTriggerRun(studyId);
  const {
    archiveStudy,
    error: archiveError,
    loading: archiving,
  } = API.Studies.useArchiveStudy();
  const {
    deleteStudy,
    error: deleteError,
    loading: deleting,
  } = API.Studies.useDeleteStudy();
  const { exportResults: exportExcel, loading: exportingExcel } =
    API.Studies.useExportRunResults("xlsx");
  const { exportResults: exportPdf, loading: exportingPdf } =
    API.Studies.useExportRunResults("pdf");
  const { exportResults: exportWord, loading: exportingWord } =
    API.Studies.useExportRunResults("docx");
  const {
    error: shareError,
    link,
    loading: sharing,
    shareStudy,
  } = API.Studies.useShareStudy(studyId);

  const steps = useMemo(
    () => [
      i18n.t("audienceBuilder.stepAudience"),
      i18n.t("dashboardNavigator.questions"),
      i18n.t("dashboardNavigator.results"),
    ],
    [i18n]
  );

  const completedSteps = useMemo(() => {
    const completed: number[] = [];
    if (totalRespondents > 0) completed.push(0);
    if (totalQuestions > 0) completed.push(1);
    if (completedRunId) completed.push(2);
    return completed;
  }, [completedRunId, totalQuestions, totalRespondents]);

  /* ── Step transition animation ── */

  const animOpacity = useSharedValue(1);
  const animTx = useSharedValue(0);

  useEffect(() => {
    if (!hasNavigated.current) {
      prevStep.current = currentStep;
      return;
    }
    const dir = currentStep > prevStep.current ? 1 : -1;
    animOpacity.value = 0;
    animTx.value = dir * 40;
    animOpacity.value = withTiming(1, { duration: 300 });
    animTx.value = withTiming(0, { duration: 300 });
    prevStep.current = currentStep;
  }, [animOpacity, animTx, currentStep]);

  const enterStyle = useAnimatedStyle(() => ({
    opacity: animOpacity.value,
    transform: [{ translateX: animTx.value }],
  }));

  const goToStep = useCallback(
    (step: number) => {
      hasNavigated.current = true;
      setCurrentStep(step);
      router.setParams({ tab: STEP_TO_TAB[step] });
    },
    [router]
  );

  // These effects react to route params / loaded data and also perform
  // navigation (router.setParams), so they stay effects but defer to a timer so
  // their state updates don't run synchronously inside the effect body.
  useEffect(() => {
    if (!runId) return;
    const timeout = setTimeout(() => {
      setCompletedRunId(null);
      setActiveRunId(runId);
      goToStep(2);
    }, 0);
    return () => clearTimeout(timeout);
  }, [runId, goToStep]);

  useEffect(() => {
    if (!study) return;

    const timeout = setTimeout(() => {
      if (study.last_run) {
        if (study.last_run.completed_at === null) {
          setActiveRunId(study.last_run.id);
        } else if (study.last_run.conclusion === "success") {
          setCompletedRunId(study.last_run.id);
        }
      }

      setTotalQuestions(study.num_questions);
      setTotalRespondents(study.num_respondents);

      if (!hasSyncedTab.current) {
        hasSyncedTab.current = true;
        let initialStep = 0;
        if (
          study.last_run?.conclusion === "success" ||
          study.last_run?.completed_at === null
        ) {
          initialStep = 2;
        } else if (study.num_questions > 0) {
          initialStep = 1;
        }
        setCurrentStep(initialStep);
        router.setParams({ tab: STEP_TO_TAB[initialStep] });
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [router, study]);

  useEffect(() => {
    if (!(tab && tab in TAB_TO_STEP)) return;
    const timeout = setTimeout(() => {
      setCurrentStep(TAB_TO_STEP[tab]);
    }, 0);
    return () => clearTimeout(timeout);
  }, [tab]);

  const handleRunSimulation = async () => {
    if (!studyId) return;
    const runId = await triggerRun();
    if (runId) goToStep(2);
  };

  const handleRunComplete = useCallback(() => {
    if (!activeRunId) return;
    const id = activeRunId;
    setTimeout(() => {
      setCompletedRunId(id);
      setActiveRunId(null);
    }, 4500);
  }, [activeRunId]);

  const handleArchiveConfirm = async () => {
    if (!studyId) return;
    const result = await archiveStudy(studyId, !isArchived);
    if (result) {
      setShowArchiveDialog(false);
      if (!isArchived) {
        router.back();
      } else {
        refresh();
      }
    }
  };

  const handleRestore = useCallback(async () => {
    if (!studyId) return;
    const result = await archiveStudy(studyId, false);
    if (result) refresh();
    return !!result;
  }, [archiveStudy, refresh, studyId]);

  const handleDeleteConfirm = async () => {
    if (!studyId) return;
    const result = await deleteStudy(studyId);
    if (result) {
      setShowDeleteDialog(false);
      router.back();
    }
  };

  const handleExportExcel = useCallback(() => {
    if (studyId && completedRunId) {
      exportExcel(studyId, completedRunId);
    }
  }, [completedRunId, exportExcel, studyId]);

  const handleExportPdf = useCallback(() => {
    if (studyId && completedRunId) {
      exportPdf(studyId, completedRunId);
    }
  }, [completedRunId, exportPdf, studyId]);

  const handleExportWord = useCallback(() => {
    if (studyId && completedRunId) {
      exportWord(studyId, completedRunId);
    }
  }, [completedRunId, exportWord, studyId]);

  const handleDuplicateClose = () => setShowDuplicateDialog(false);

  const handleShare = useCallback(async () => {
    setShowShareDialog(true);
    if (link) return;
    if (
      study?.share_link &&
      (!study.share_expires_at || new Date(study.share_expires_at) > new Date())
    ) {
      return;
    }
    shareStudy(30);
  }, [link, shareStudy, study]);

  /* ── Menu actions ── */

  const menuActions = useMemo(() => {
    if (isArchived) {
      const actions = [
        ...(!isReadOnly
          ? [
              {
                color: "typography" as const,
                icon: RotateCcw,
                label: i18n.t("misc.restore"),
                onPress: () => setShowArchiveDialog(true),
              },
            ]
          : []),
        ...(canDelete
          ? [
              {
                color: "error" as const,
                divider: !isReadOnly as true | undefined,
                icon: Trash2,
                label: i18n.t("misc.deletePermanently"),
                onPress: () => setShowDeleteDialog(true),
              },
            ]
          : []),
      ];
      return actions.length > 0 ? actions : null;
    }

    return [
      {
        color: "typography" as const,
        icon: Copy,
        label: i18n.t("misc.duplicate"),
        onPress: () => setShowDuplicateDialog(true),
      },
      ...(!isReadOnly && organization
        ? [
            {
              color: "typography" as const,
              divider: true as const,
              icon: UserPlus,
              label: i18n.t("sharing.share"),
              onPress: () => setShowShareModal(true),
            },
          ]
        : []),
      {
        color: "typography" as const,
        disabled: !completedRunId || sharing,
        icon: Share2,
        label: sharing
          ? i18n.t("studies.sharing")
          : i18n.t("studies.shareResults"),
        onPress: handleShare,
      },
      {
        color: "typography" as const,
        disabled: !completedRunId || exportingPdf,
        divider: true as const,
        icon: FileText,
        label: exportingPdf
          ? i18n.t("studies.exporting")
          : i18n.t("studies.exportPDF"),
        onPress: handleExportPdf,
      },
      {
        color: "typography" as const,
        disabled: !completedRunId || exportingWord,
        icon: FileType2,
        label: exportingWord
          ? i18n.t("studies.exporting")
          : i18n.t("studies.exportWord"),
        onPress: handleExportWord,
      },
      {
        color: "typography" as const,
        disabled: !completedRunId || exportingExcel,
        icon: FileSpreadsheet,
        label: exportingExcel
          ? i18n.t("studies.exporting")
          : i18n.t("studies.exportExcel"),
        onPress: handleExportExcel,
      },
      ...(!isReadOnly
        ? [
            {
              color: "error" as const,
              divider: true as const,
              icon: Archive,
              label: i18n.t("misc.archive"),
              onPress: () => setShowArchiveDialog(true),
            },
          ]
        : []),
      ...(canDelete
        ? [
            {
              color: "error" as const,
              divider: isReadOnly as true | undefined,
              icon: Trash2,
              label: i18n.t("misc.deletePermanently"),
              onPress: () => setShowDeleteDialog(true),
            },
          ]
        : []),
    ];
  }, [
    canDelete,
    completedRunId,
    exportingExcel,
    exportingPdf,
    exportingWord,
    handleExportExcel,
    handleExportPdf,
    handleExportWord,
    handleShare,
    i18n,
    isArchived,
    isReadOnly,
    organization,
    sharing,
  ]);

  /* ── Error gate ── */

  if (error) {
    return (
      <Box className="flex-1 px-4 lg:px-8 pb-8">
        <ErrorWithRetry
          error={error || i18n.t("misc.errorOccurred")}
          onRetry={refresh}
          title={i18n.t("studies.errorLoadingStudy")}
        />
      </Box>
    );
  }

  /* ── Render ── */

  /* Questions step pieces — extracted so the same editor + launch card can be
     laid out two ways: a fixed side card on desktop, and a pinned collapsible
     card above the scrolling editor on mobile. */
  const studyEditor = (
    <StudyEditor
      disabled={isReadOnly || isArchived || !!activeRunId || !!completedRunId}
      onErrorCountChange={setErrorCount}
      onNumQuestionsChange={setTotalQuestions}
      studyId={studyId}
      useCase={study?.use_case ?? null}
    />
  );

  const launchTitle = completedRunId
    ? i18n.t("audienceBuilder.studyComplete")
    : activeRunId
      ? i18n.t("audienceBuilder.studyInProgress")
      : i18n.t("audienceBuilder.readyToLaunch");

  const launchBody = (
    <>
      <Text className="text-sm text-white/70 text-center mb-6">
        {completedRunId
          ? i18n.t("audienceBuilder.completedSummary", {
              count: study?.num_groups ?? 0,
              respondents: i18n.numberToDelimited(totalRespondents, {
                delimiter: browserLocale.digitGroupingSeparator!,
              }),
            })
          : activeRunId
            ? i18n.t("audienceBuilder.inProgressSummary")
            : `${i18n.t("audienceBuilder.launchSummary", {
                count: study?.num_groups ?? 0,
                respondents: i18n.numberToDelimited(totalRespondents, {
                  delimiter: browserLocale.digitGroupingSeparator!,
                }),
              })} ${i18n.t("audienceBuilder.launchEstimate", {
                count: estimatedMinutes,
              })}`}
      </Text>
      {completedRunId ? (
        <Button
          className="w-full rounded-full py-3.5 gap-2 bg-primary-500 data-[hover=true]:bg-primary-400 data-[active=true]:bg-primary-600"
          onPress={() => goToStep(2)}
        >
          <BarChart3 color="rgb(var(--color-typography-0))" size={16} />
          <ButtonText className="text-white font-medium">
            {i18n.t("audienceBuilder.seeResults")}
          </ButtonText>
        </Button>
      ) : activeRunId ? (
        <Button
          className="w-full rounded-full py-3.5 gap-2 bg-primary-500 data-[hover=true]:bg-primary-400 data-[active=true]:bg-primary-600"
          onPress={() => goToStep(2)}
        >
          <BarChart3 color="rgb(var(--color-typography-0))" size={16} />
          <ButtonText className="text-white font-medium">
            {i18n.t("audienceBuilder.seeProgress")}
          </ButtonText>
        </Button>
      ) : (
        <>
          {!isReadOnly && totalQuestions > 0 && totalRespondents > 0 && (
            <Text className="text-xs text-white/50 text-center mb-3">
              {i18n.t("billing.estimatedCost", {
                count:
                  totalQuestions *
                  totalRespondents *
                  (study?.type === "qualitative" ? 2 : 1),
              })}
            </Text>
          )}
          <Button
            className="mt-3 w-full rounded-full py-3.5 gap-2 bg-primary-500 data-[hover=true]:bg-primary-400 data-[active=true]:bg-primary-600"
            isDisabled={
              isReadOnly ||
              isArchived ||
              triggering ||
              totalQuestions === 0 ||
              totalRespondents === 0 ||
              errorCount > 0
            }
            onPress={handleRunSimulation}
          >
            <Rocket color="rgb(var(--color-typography-0))" size={16} />
            <ButtonText className="text-white font-medium">
              {triggering
                ? i18n.t("studies.triggeringSimulation")
                : i18n.t("audienceBuilder.launchStudy")}
            </ButtonText>
          </Button>
          {!isReadOnly && errorCount > 0 && (
            <Text className="text-xs text-red-400 text-center mt-3">
              {i18n.t("audienceBuilder.launchBlockedByErrors", {
                count: errorCount,
              })}
            </Text>
          )}
          {isReadOnly && (
            <Text className="text-xs text-white/50 text-center mt-3">
              {i18n.t("dashboard.noLaunchPermission")}
            </Text>
          )}
          {!isReadOnly && triggerError && (
            <Text className="text-xs text-red-400 text-center mt-3">
              {triggerError}
            </Text>
          )}
        </>
      )}
    </>
  );

  return (
    <StudyProvider studyId={studyId} studyType={study?.type || null}>
      <VStack className="flex-1 px-4 lg:px-8">
        {/* Step indicator + ellipsis menu */}
        <HStack className="items-center justify-between h-[50px]">
          <StepIndicator
            completedSteps={completedSteps}
            current={currentStep}
            loading={loading}
            onStepPress={goToStep}
            steps={steps}
          />

          {menuActions && (
            <ActionMenu
              actions={menuActions}
              trigger={(triggerProps) => (
                <Button
                  {...triggerProps}
                  className="p-3 rounded-lg border border-outline-200 dark:border-outline-700 bg-white dark:bg-outline-950"
                  variant="outline"
                >
                  <ButtonIcon
                    as={EllipsisVertical}
                    className="text-typography-700 dark:text-typography-300"
                  />
                </Button>
              )}
            />
          )}
        </HStack>

        {isArchived ? (
          <ArchivedBanner
            canRestore={!isReadOnly}
            onRestore={handleRestore}
            resourceType="study"
          />
        ) : (
          <Box className="mb-8" />
        )}

        {/* Step content */}
        <Box className="flex-1 min-h-0">
          {/* Step 0: Audience / Groups */}
          <Animated.View
            style={[
              { flex: 1 },
              currentStep !== 0 ? { display: "none" } : undefined,
              currentStep === 0 ? enterStyle : undefined,
            ]}
          >
            <Groups
              disabled={
                isReadOnly || isArchived || !!activeRunId || !!completedRunId
              }
              onContinue={() => goToStep(1)}
              onTotalSizeChange={setTotalRespondents}
              resourceId={studyId}
              resourceType="study"
            />
          </Animated.View>

          {/* Step 1: Questions */}
          <Animated.View
            style={[
              { flex: 1 },
              currentStep !== 1 ? { display: "none" } : undefined,
              currentStep === 1 ? enterStyle : undefined,
            ]}
          >
            {isDesktop ? (
              <HStack className="gap-2 flex-1 min-h-0">
                <VStack className="w-full flex-1 min-h-0 lg:flex-[3]">
                  {studyEditor}
                </VStack>

                <VStack className="w-full lg:flex-1">
                  {loading ? (
                    <Skeleton className="h-[200px] w-full rounded-2xl" />
                  ) : (
                    <Box className="bg-typography-900 rounded-2xl p-8 sticky top-6">
                      <Text className="text-[13px] uppercase tracking-widest text-white/50 font-medium mb-4 text-center">
                        {launchTitle}
                      </Text>
                      {launchBody}
                    </Box>
                  )}
                </VStack>
              </HStack>
            ) : (
              <VStack className="flex-1 min-h-0 gap-3">
                {loading ? (
                  <Skeleton className="h-[120px] w-full rounded-2xl" />
                ) : (
                  <MobileSummaryCard title={launchTitle}>
                    {launchBody}
                  </MobileSummaryCard>
                )}
                <VStack className="w-full flex-1 min-h-0">{studyEditor}</VStack>
              </VStack>
            )}
          </Animated.View>

          {/* Step 2: Results */}
          <Animated.View
            style={[
              { flex: 1 },
              currentStep !== 2 ? { display: "none" } : undefined,
              currentStep === 2 ? enterStyle : undefined,
            ]}
          >
            <Box className="flex-1 min-h-0">
              {completedRunId ? (
                <RunProvider runId={completedRunId}>
                  <RunResults />
                </RunProvider>
              ) : activeRunId ? (
                <Run
                  onComplete={handleRunComplete}
                  onRetry={handleRunSimulation}
                  resourceId={studyId}
                  resourceType="study"
                  retryError={triggerError}
                  retrying={triggering}
                  runId={activeRunId}
                />
              ) : loading ? (
                <RunResultsSkeleton />
              ) : (
                <Center className="flex-1">
                  <VStack className="items-center gap-3">
                    <BarChart3 className="text-typography-300" size={48} />
                    <Text className="text-lg font-medium text-typography-700">
                      {i18n.t("studies.noResultsYet")}
                    </Text>
                    <Text className="text-sm text-typography-500">
                      {i18n.t("studies.noResultsYetDescription")}
                    </Text>
                  </VStack>
                </Center>
              )}
            </Box>
          </Animated.View>
        </Box>

        {/* Archive / Restore Confirmation Dialog */}
        <DeleteDialog
          body={
            isArchived
              ? i18n.t("studies.confirmRestoreMessage")
              : i18n.t("studies.confirmArchiveMessage")
          }
          deleteButtonText={
            isArchived ? i18n.t("misc.restore") : i18n.t("misc.archive")
          }
          error={archiveError}
          isOpen={showArchiveDialog}
          loading={archiving}
          loadingText={
            isArchived ? i18n.t("misc.restoring") : i18n.t("misc.archiving")
          }
          onCancel={() => setShowArchiveDialog(false)}
          onDelete={handleArchiveConfirm}
          title={
            isArchived
              ? i18n.t("studies.restoreStudy")
              : i18n.t("studies.archiveStudy")
          }
        />

        {/* Delete Permanently Dialog */}
        <DeleteDialog
          body={i18n.t("studies.confirmDeletePermanentlyMessage")}
          deleteButtonText={i18n.t("misc.deletePermanently")}
          error={deleteError}
          isOpen={showDeleteDialog}
          loading={deleting}
          loadingText={i18n.t("misc.deleting")}
          onCancel={() => setShowDeleteDialog(false)}
          onDelete={handleDeleteConfirm}
          title={i18n.t("studies.deleteStudyPermanently")}
        />

        {/* Duplicate Study Dialog */}
        <CreateStudyDialog
          folderId={study?.folder_id}
          initialDescription={study?.description || undefined}
          initialName={
            study ? i18n.t("studies.copyOf", { name: study.name }) : undefined
          }
          isOpen={showDuplicateDialog}
          onClose={handleDuplicateClose}
          studyId={studyId}
        />

        {/* Internal Share Modal */}
        {showShareModal && study && (
          <ShareModal
            isOpen
            onClose={() => setShowShareModal(false)}
            ownerId={study.created_by_id}
            resourceId={study.id}
            resourceName={study.name}
            resourceType="study"
          />
        )}

        {/* Share Link Dialog */}
        <ShareResultsModal
          error={shareError}
          isOpen={showShareDialog}
          link={study?.share_link ?? link}
          loading={sharing}
          onClose={() => setShowShareDialog(false)}
          onRetry={() => shareStudy(30)}
        />

        {/* Floating Chat Button */}
        {/* {completedRunId && currentStep === 2 && (
          <StudyChatButton runId={completedRunId} studyId={studyId} />
        )} */}
      </VStack>
    </StudyProvider>
  );
};

export default Study;
